/* @jsx h */
import { defineComponent, ref, computed, h } from "vue";
import {
  CheckoutData,
  OrderItem,
  CheckoutDiscount,
  CheckoutDiscountComponent,
} from "@lemmonhq/types";
import type { DiscountType } from "@lemmonhq/types";
import { LemmonModal } from "@lemmonhq/uikit";
import { calculateOrderItemTotal } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";
import { useQuery } from "@tanstack/vue-query";
import { useTrpc, useAuth } from "@app/vue/providers/index";

export type DiscountModalProps = {
  onClose: () => void;
  onConfirm: (discount: CheckoutDiscount | undefined) => void;
  total: number;
  checkout: CheckoutData;
};

export const DiscountModal = defineComponent<DiscountModalProps>({
  name: "DiscountModal",
  props: ["onClose", "onConfirm", "total", "checkout"],
  setup(props) {
    const auth = useAuth();
    const view = ref<"manual" | "saved">("manual");
    const type = ref<"percentage" | "amount">("amount");
    const discountType = ref<"standard" | "fork" | "employee" | "owner">(
      "standard",
    );
    const { t } = useLocalization();

    const value = ref<number | undefined>();
    const selectedDiscountId = ref<string | null>(null);
    const customValue = ref<string>("");
    const { checkout } = props;
    const trpc = useTrpc();

    const { data: savedDiscountTypes, isPending: savedDiscountsPending } =
      useQuery({
        queryKey: ["discountTypes", "list"],
        queryFn: () => trpc.discountTypes.list.query(),
      });

    const selectedDiscount = computed(() => {
      if (!selectedDiscountId.value || !savedDiscountTypes.value) return null;
      return (
        savedDiscountTypes.value.find(
          (discount) => discount.id === selectedDiscountId.value,
        ) || null
      );
    });

    const discountTypes = [
      { label: t("pos.discount.standard"), value: "standard" },
      { label: t("pos.discount.the_fork"), value: "fork" },
      { label: t("pos.discount.passport_gourmand"), value: "fork" },
      { label: t("pos.discount.employee"), value: "employee" },
      { label: t("pos.discount.owner"), value: "owner" },
    ];

    const getRestaurantItems = computed(() => {
      if (!checkout.payload) return [];
      console.log("checkout.payload", checkout.payload);
      if (checkout.payload.type === "cart") {
        return checkout.payload.object.items.filter(
          (item) => !item.itemType || item.itemType == "food",
        );
      } else {
        const orders = checkout.payload.object.orders;
        const selectedItems =
          checkout.payload.object.type === "partial"
            ? checkout.payload.object.items
            : undefined;

        let items: OrderItem[] = [];
        orders.forEach((order) => {
          if (!order.items) return;

          order.items.forEach((item) => {
            // If there are selected items, only include those
            if (selectedItems) {
              const selectedItem = selectedItems.find(
                (si) => si.id === item.id,
              );
              if (
                selectedItem &&
                (!item.product.item_type || item.product.item_type == "food")
              ) {
                items.push({
                  ...item,
                  quantity: selectedItem.quantity,
                });
              }
            } else if (
              !item.product.item_type ||
              item.product.item_type == "food"
            ) {
              items.push(item);
            }
          });
        });
        return items;
      }
    });

    const restaurantTotal = computed(() => {
      if (!checkout.payload) return 0;
      if (checkout.payload.type === "cart") {
        return getRestaurantItems.value.reduce(
          (sum, item) => sum + parseFloat(item.price as string) * item.quantity,
          0,
        );
      } else {
        return getRestaurantItems.value.reduce(
          (sum, item) => sum + calculateOrderItemTotal(item as any),
          0,
        );
      }
    });

    const effectiveSavedDiscountValue = computed(() => {
      if (!selectedDiscount.value) return 0;

      if (selectedDiscount.value.allow_changes && customValue.value) {
        const parsed = parseFloat(customValue.value);
        return isNaN(parsed) ? selectedDiscount.value.default.value : parsed;
      }

      return selectedDiscount.value.default.value;
    });

    const isSavedDiscountValid = computed(() => {
      if (!selectedDiscount.value) return false;

      const savedValue = effectiveSavedDiscountValue.value;
      if (selectedDiscount.value.default.type === "percentage") {
        return savedValue >= 0 && savedValue <= 100;
      }

      return savedValue >= 0;
    });

    const applySavedDiscount = (
      discount: DiscountType,
      overrideValue?: number,
    ) => {
      const savedValue = overrideValue ?? discount.default.value;
      const isPercentage = discount.default.type === "percentage";
      // props.total is the promotion-adjusted base; a fixed discount can never
      // exceed it (otherwise it would drive the total negative).
      const discountAmount = isPercentage
        ? (props.total * savedValue) / 100
        : Math.min(savedValue, props.total);
      const isValid = isPercentage
        ? savedValue >= 0 && savedValue <= 100
        : savedValue >= 0;

      if (!isValid) return;

      const component: CheckoutDiscountComponent = {
        source: "pre-defined",
        value_type: discount.default.type,
        input_value: savedValue,
        discount_amount: discountAmount,
        discount_type_id: discount.id,
        category: discount.name,
        metadata: { cart_items_ids: [] },
      };

      props.onConfirm({
        type: discount.default.type,
        value: savedValue,
        amount: discountAmount,
        discount_type_id: discount.id,
        category: discount.name,
        subcomponents: [component],
      });

      props.onClose();
    };

    const handleSubmit = (e: Event) => {
      e.preventDefault();

      if (view.value === "saved") {
        if (!selectedDiscount.value || !isSavedDiscountValid.value) return;
        applySavedDiscount(
          selectedDiscount.value,
          effectiveSavedDiscountValue.value,
        );
        return;
      }

      const numValue = parseFloat(value.value + "");
      console.log("numValue", numValue, discountType.value, type.value);
      if (isNaN(numValue)) {
        return;
      }

      if (discountType.value === "fork") {
        // Fork discounts are always percentage-based, applied to restaurant items only
        const amount = (restaurantTotal.value * numValue) / 100;
        const component: CheckoutDiscountComponent = {
          source: "manual",
          value_type: "percentage",
          input_value: numValue,
          discount_amount: amount,
          category: discountType.value,
          metadata: { cart_items_ids: [] },
        };
        props.onConfirm({
          type: "percentage", // Ensure it's percentage
          category: discountType.value,
          value: numValue,
          amount, // Correctly calculated
          subcomponents: [component],
        });
      } else if (type.value === "percentage" && numValue <= 100) {
        const amount = (props.total * numValue) / 100;
        const component: CheckoutDiscountComponent = {
          source: "manual",
          value_type: "percentage",
          input_value: numValue,
          discount_amount: amount,
          category: discountType.value,
          metadata: { cart_items_ids: [] },
        };
        props.onConfirm({
          type: "percentage",
          category: discountType.value,
          value: numValue,
          amount,
          subcomponents: [component],
        });
      } else if (type.value === "amount") {
        // Cap at the promotion-adjusted base so a fixed discount larger than
        // what's owed can't drive the total negative.
        const amount = Math.min(numValue, props.total);
        const component: CheckoutDiscountComponent = {
          source: "manual",
          value_type: "fixed-amount",
          input_value: numValue,
          discount_amount: amount,
          category: discountType.value,
          metadata: { cart_items_ids: [] },
        };
        props.onConfirm({
          type: "fixed-amount",
          category: discountType.value,
          value: numValue,
          amount,
          subcomponents: [component],
        });
      }

      props.onClose();
    };

    return () => {
      const val = parseFloat(value.value + "");
      const hasSavedDiscounts =
        savedDiscountTypes.value && savedDiscountTypes.value.length > 0;
      const canSubmit =
        view.value === "manual"
          ? Number.isFinite(val)
          : isSavedDiscountValid.value;

      return (
        <div>
          <LemmonModal
            title={t("pos.discount.add_discount")}
            onClose={props.onClose}
            renderButtons={() => (
              <div class="p-4 border-t ">
                <div class="flex justify-between gap-2 flex-wrap">
                  <button
                    onClick={() =>
                      (view.value =
                        view.value === "manual" ? "saved" : "manual")
                    }
                    class="btn btn-neutral"
                    type="button"
                  >
                    {view.value === "manual"
                      ? t("settings.discount_types_page.page_title")
                      : t("pos.discount.add_discount")}
                  </button>
                  <div class="flex gap-2 ml-auto">
                    <button
                      onClick={(e) => props.onClose()}
                      class="btn btn-neutral"
                      type="button"
                    >
                      {t("pos.discount.cancel_button")}
                    </button>
                    <button
                      onClick={handleSubmit}
                      class="btn btn-primary"
                      type="submit"
                      disabled={!canSubmit}
                    >
                      {t("pos.discount.apply_discount")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          >
            <div class="py-6 px-8 space-y-8">
              {view.value === "manual" ? (
                <>
                  <div>
                    <label class="block text-sm font-medium  mb-2">
                      {t("pos.discount.discount_type")}
                    </label>
                    <select
                      class="select select-bordered w-full bg-gray-800 text-accent"
                      value={discountType.value}
                      onChange={(e) => {
                        discountType.value = (e.target as HTMLSelectElement)
                          .value as any;
                        // Reset value when changing discount type
                        value.value = undefined;
                        // For fork discount, force percentage type
                        if ((e.target as HTMLSelectElement).value === "fork") {
                          type.value = "percentage";
                        }
                      }}
                    >
                      {discountTypes.map((discType) => (
                        <option key={discType.value} value={discType.value}>
                          {discType.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {discountType.value !== "fork" && (
                    <div class="flex gap-4">
                      <button
                        type="button"
                        onClick={() => (type.value = "amount")}
                        class={`btn flex-1 ${
                          type.value === "amount"
                            ? "btn-primary"
                            : "btn-neutral"
                        }`}
                      >
                        {t("pos.discount.discount_amount")}
                      </button>
                      <button
                        type="button"
                        onClick={() => (type.value = "percentage")}
                        class={`btn flex-1 ${
                          type.value === "percentage"
                            ? "btn-primary"
                            : "btn-neutral"
                        }`}
                      >
                        {t("pos.discount.percentage")}
                      </button>
                    </div>
                  )}
                  <div>
                    <label class="block text-sm font-medium  mb-2">
                      {type.value === "percentage"
                        ? `${t("pos.discount.percentage")} - %`
                        : `${t("pos.discount.amount")} - ${auth.user.value?.restaurant.currency || "CHF"}`}
                    </label>
                    <input
                      type="number"
                      v-model={value.value}
                      class="w-full p-2 input border  rounded text-accent"
                      step="0.01"
                      min="0"
                      max={type.value === "percentage" ? "100" : undefined}
                      placeholder={
                        type.value === "percentage" ? "0-100" : "0.00"
                      }
                    />
                    {discountType.value === "fork" && value.value && (
                      <div class="mt-2 text-sm ">
                        {t("pos.discount.fork_discount_message")} (
                        {auth.user.value?.restaurant.currency || "CHF"}{" "}
                        {restaurantTotal.value.toFixed(2)})
                      </div>
                    )}
                  </div>
                </>
              ) : savedDiscountsPending.value ? (
                <div class="py-12 flex justify-center items-center">
                  <span class="loading loading-spinner loading-lg"></span>
                </div>
              ) : !hasSavedDiscounts ? (
                <div class="text-center py-12 rounded-2xl border border-base-300/80 bg-base-100">
                  <div class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-base-200/70 mb-4">
                    <span class="material-symbols-outlined text-3xl text-base-content/35">
                      local_offer
                    </span>
                  </div>
                  <p class="text-base-content/65 text-sm max-w-sm mx-auto leading-6">
                    {t("settings.discount_types_page.empty_description")}
                  </p>
                </div>
              ) : (
                <>
                  <div class="space-y-3 max-h-96 overflow-y-auto pr-1">
                    {savedDiscountTypes.value?.map((discount) => {
                      const isSelected =
                        selectedDiscountId.value === discount.id;
                      return (
                        <button
                          key={discount.id}
                          type="button"
                          onClick={() => {
                            selectedDiscountId.value = discount.id;
                            customValue.value = "";
                            if (!discount.allow_changes) {
                              applySavedDiscount(discount);
                            }
                          }}
                          class={`w-full p-5 rounded-2xl border transition-all duration-150 text-left l-box-shadow ${
                            isSelected
                              ? "border-primary/50 bg-base-100/80 shadow-[0_0_0_1px_rgba(var(--p),0.18)]"
                              : "border-base-300/90 bg-base-100/80"
                          }`}
                        >
                          <div class="flex items-start gap-4">
                            <div class="flex-1 min-w-0">
                              <div class="flex items-start justify-between gap-3">
                                <div class="min-w-0">
                                  <div class="font-medium text-accent leading-5 tracking-[0.01em]">
                                    {discount.name}
                                  </div>
                                  <div class="flex items-center gap-2.5 flex-wrap mt-2">
                                    {discount.allow_changes ? (
                                      <span class="badge badge-sm badge-outline gap-1 font-normal bg-purple-500/10 text-purple-500 border border-purple-500/20">
                                        <span class="material-symbols-outlined text-[11px]">
                                          tune
                                        </span>
                                        {t(
                                          "settings.discount_types_page.badge_flexible",
                                        )}
                                      </span>
                                    ) : (
                                      <span class="badge badge-sm badge-outline gap-1 font-normal bg-purple-500/10 text-purple-500 border border-purple-500/20">
                                        <span class="material-symbols-outlined text-[11px]">
                                          lock
                                        </span>
                                        {t(
                                          "settings.discount_types_page.badge_fixed",
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div class="flex items-center gap-2 flex-shrink-0">
                                  <div
                                    class={`text-base font-bold tracking-[0.01em] px-3 py-1.5 rounded-lg border ${
                                      isSelected
                                        ? "text-primary border-primary/35 bg-primary/10"
                                        : "text-base-content/85 border-base-300/90 bg-base-200/50"
                                    }`}
                                  >
                                    {discount.default.type === "percentage"
                                      ? `${discount.default.value}%`
                                      : `${auth.user.value?.restaurant.currency || "CHF"} ${discount.default.value.toFixed(
                                          2,
                                        )}`}
                                  </div>
                                  {isSelected && (
                                    <span class="material-symbols-outlined text-primary/90 text-[18px] flex-shrink-0">
                                      check
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedDiscount.value?.allow_changes && (
                    <div class="bg-base-100 rounded-2xl p-5 border border-base-300/90">
                      <label class="block text-xs font-semibold uppercase tracking-[0.14em] text-base-content/55 mb-3.5 flex items-center gap-2">
                        <span class="material-symbols-outlined text-primary text-[15px]">
                          edit
                        </span>
                        {selectedDiscount.value.default.type === "percentage"
                          ? t("pos.discount.percentage")
                          : t("pos.discount.amount")}
                      </label>
                      <div class="relative">
                        <input
                          type="number"
                          value={customValue.value}
                          onInput={(e) =>
                            (customValue.value = (
                              e.target as HTMLInputElement
                            ).value)
                          }
                          class="w-full px-4 py-3.5 input border border-base-300 rounded-xl text-base-content bg-base-100 focus:border-primary focus:outline-none text-[15px] font-medium"
                          step="0.01"
                          min="0"
                          max={
                            selectedDiscount.value.default.type === "percentage"
                              ? "100"
                              : undefined
                          }
                          placeholder={
                            selectedDiscount.value.default.type === "percentage"
                              ? `Default: ${selectedDiscount.value.default.value}%`
                              : `Default: ${auth.user.value?.restaurant.currency || "CHF"} ${selectedDiscount.value.default.value.toFixed(
                                  2,
                                )}`
                          }
                        />
                        <div class="absolute right-4 top-1/2 -translate-y-1/2 text-base-content/45 text-xs font-semibold tracking-wide">
                          {selectedDiscount.value.default.type === "percentage"
                            ? "%"
                            : auth.user.value?.restaurant.currency || "CHF"}
                        </div>
                      </div>
                      <div class="mt-3.5 text-xs text-base-content/55 flex items-center gap-1.5">
                        <span class="material-symbols-outlined text-[13px]">
                          info
                        </span>
                        {t("settings.discount_types_page.staff_can_adjust")}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </LemmonModal>
        </div>
      );
    };
  },
});
